/**
 * Read-only snapshot of a sample "Global Absence Recording - Single Assignment"
 * approval ruleset, transcribed from the BPM Worklist Task Configuration (Assignees
 * tab) screenshot. AMX IF/THEN rules are sealed on this SaaS pod, so this is a
 * manually captured snapshot for viewing — not a live read. Update if the tenant changes.
 *
 * THEN entries are participant types from the BPM "Approvers" palette.
 */

export const APPROVER_PALETTE = Object.freeze([
  { type: 'applicationRole', label: 'Application Role' },
  { type: 'approvalGroups', label: 'Approval Groups' },
  { type: 'jobLevelLineManager', label: 'Job-Level-Based Line Manager Hierarchy' },
  { type: 'managementHierarchy', label: 'Management Hierarchy' },
  { type: 'positionHierarchy', label: 'Position Hierarchy' },
  { type: 'representative', label: 'Representative' },
  { type: 'selfAutoApprove', label: 'Self Auto Approve' },
  { type: 'users', label: 'Users' },
]);

const MH = 'managementHierarchy';
const REP = 'representative';
const SAA = 'selfAutoApprove';

/** Each rule: name + ordered THEN approver participant types (the routing stages). */
export const ABSENCE_APPROVAL_RULES_SNAPSHOT = Object.freeze({
  source: 'snapshot',
  capturedAt: '2026-05-30',
  task: {
    name: 'Global Absence Recording - Single Assignment',
    code: 'AbsencesApprovalsTask',
    taskKey: 'global-absence-recording-single-assignment',
    category: 'absence',
  },
  note:
    'Read-only snapshot transcribed from BPM Worklist Task Configuration. AMX rules are not readable via API on this pod, so values are captured from the tenant UI and may need re-verification.',
  rules: [
    { name: 'E_LM', then: [MH, MH] },
    { name: 'E_HR_FYIHR_FYILM', then: [REP, MH] },
    { name: 'E_FYILM', then: [MH] },
    { name: 'E_LM_FYIHR_FYILM', then: [MH, REP, MH] },
    { name: 'E_HR_FYILM', then: [REP, MH] },
    { name: 'E_LM_HR_RW', then: [MH, REP, REP] },
    { name: 'E_LM_HR_RW1', then: [SAA, MH] },
    { name: 'E_HR', then: [MH, REP, MH] },
    { name: 'E_HR1', then: [SAA, MH] },
    { name: 'LM_FYIHR', then: [SAA, REP] },
    { name: 'HR_FYILM', then: [SAA, MH] },
    { name: 'E_LM_NL', then: [MH, REP] },
    { name: 'LM_FYI_E_NL', then: [SAA, REP] },
    { name: 'HR_LM_NL', then: [MH, REP, REP] },
    { name: 'E_LM_HR_NL', then: [MH, REP, REP, REP] },
    { name: 'E_LM_PT', then: [MH] },
    { name: 'LM_E_PT', then: [SAA] },
    { name: 'HRBP_PT', then: [SAA] },
    { name: 'E_LM_HRADMIN_PT', then: [MH, REP] },
    { name: 'LM_HRADMIN_PT', then: [REP] },
    { name: 'HRBP_HRADMIN_PT', then: [REP] },
    {
      name: 'E_LM_HRBP_HRADMIN_PT',
      then: [MH, REP, REP],
      ifDetail: {
        statusCode: 'E_LM_HRBP_HRADMIN_PT',
        absenceTypes: [
          'PT Education Leave 2',
          'PT Family Leave 1',
          'PT Flexibility',
          'PT Paid Leave 2',
          'PT Paid Leave 3',
          'PT Unpaid Leave 2',
          'PT Unpaid Leave 1',
        ],
      },
    },
    {
      name: 'LM_HRBP_HRADMIN_PT',
      then: [REP, REP],
      thenRefs: ['HD_HRBP', 'HD_HRBP'],
      thenStages: [
        {
          responseType: 'Required',
          participants: {
            users: 'GetRepresentative("HD_HRBP", Task.payload.getRepresentativeListResponse.result)',
            groups: 'null',
            applicationRole: 'null',
          },
        },
        {
          responseType: 'Required',
          participants: {
            users: 'GetRepresentative("HD_HRBP", Task.payload.getRepresentativeListResponse.result)',
            groups: 'null',
            applicationRole: 'null',
          },
        },
      ],
    },
    {
      name: 'HRBP_E_PT',
      then: [SAA],
      autoApprove: true,
      ifText: 'absenceType is "PT Holiday < 1 Year" OR absenceType is "PT Compensatory Rest"',
      thenSpec: {
        responseType: 'Required',
        levels: 1,
        startingParticipant: 'HierarchyBuilder.getPrincipal(Task.payload.absencesApprovalsRequest…)',
        topParticipant: 'HierarchyBuilder.getPrincipal(Task.payload.absencesApprovalsRequest…)',
        autoActionEnabled: true,
        autoAction: '"APPROVE"',
      },
    },
  ],
});

export default ABSENCE_APPROVAL_RULES_SNAPSHOT;
